import http, { handleApiResponse } from "@/services/http";
import ApiResponse from "@/models/ApiResponse";
import {
  BankPreAccError,
  BankPreAccLookups,
  BankPreAccReAccountResult,
  BankPreAccSetStatusResult,
} from "../models/BankPreAccounting";
import {
  BankPreAccCar,
  BankPreAccEditorData,
  BankPreAccPo,
  BankPreAccProject,
  BankPreAccSaveRequest,
  BankPreAccSaveResult,
  BankPreAccTool,
  BankPreAccUploadFlagsResult,
  BankPreAccUploadItem,
  BankPreAccValidate,
} from "../models/BankPreAccEditing";

export default class BankPreAccountingApi {
  // Böngésző lookupok (kontír-státuszok, periódusok)
  static getLookups(): Promise<BankPreAccLookups> {
    return http
      .get<ApiResponse<BankPreAccLookups>>(`bank-pre-accounting/lookups`)
      .then(handleApiResponse);
  }

  // Egy kivonat kontírozási hibalistája (K2D pb_err → mmaccbankdocerr)
  static getErrors(id: number): Promise<BankPreAccError[]> {
    return http
      .get<ApiResponse<BankPreAccError[]>>(`bank-pre-accounting/${id}/errors`)
      .then(handleApiResponse);
  }

  // A kijelölt kivonatok (újra)kontírozása a BÖNGÉSZŐBŐL (K2D pb_cont → spm_bank_cont);
  // kivonatonként külön tranzakció fut, a hibák a failures listában jönnek vissza
  static reAccount(statements: number[]): Promise<BankPreAccReAccountResult> {
    return http
      .post<ApiResponse<BankPreAccReAccountResult>>(`bank-pre-accounting/re-account`, {
        statements,
      })
      .then(handleApiResponse);
  }

  // EGY kivonat újrakontírozása a SZERKESZTŐBŐL (K2D pb_recontir). Külön végpont,
  // mert a K2D-ben itt bővebb a kontír státusz kapu, mint a böngésző csoportos
  // gombjánál: a 3-as "Feladható" státusz is újrakontírozható.
  static reAccountSingle(statementId: number): Promise<BankPreAccReAccountResult> {
    return http
      .post<ApiResponse<BankPreAccReAccountResult>>(
        `bank-pre-accounting/${statementId}/re-account`, {})
      .then(handleApiResponse);
  }

  //---------- Státusz állítás (K2D m_bank_cash.m_status menü) ----------//

  // Csoportos kontír státusz állítás (K2D ue_acc_status bank ága: "Nem adható fel" = 2,
  // "Feladható" = 3, "Nem feladandó" = 9). A művelet egy tranzakció, a nem megfelelő
  // státuszú kivonatok a failures listában jönnek vissza.
  static setStatuses(status: number, statements: number[]): Promise<BankPreAccSetStatusResult> {
    return http
      .post<ApiResponse<BankPreAccSetStatusResult>>(
        `bank-pre-accounting/set-status/${status}`,
        { statements }
      )
      .then(handleApiResponse);
  }

  // Csoportos "Feladva": a kijelölt (Feladható) kivonatok feladása a belső főkönyvbe
  // (K2D Case 4 → spmacc_bookhanding). allowAutoPromote = az "egyből feladva"
  // jogosultság. Bármely kivonat hibája az egész műveletet visszagördíti.
  static book(
    statements: number[],
    allowAutoPromote: boolean
  ): Promise<BankPreAccSetStatusResult> {
    return http
      .post<ApiResponse<BankPreAccSetStatusResult>>(`bank-pre-accounting/book`, {
        statements,
        allowAutoPromote,
      })
      .then(handleApiResponse);
  }

  // Csoportos "Feladásra előkészítve / visszavétel a főkönyvből" (K2D Case 5, 31):
  // a Feladható kivonat előkészítettbe kerül, a már feladott (4/6) vissza a főkönyvből.
  static takeBack(statements: number[]): Promise<BankPreAccSetStatusResult> {
    return http
      .post<ApiResponse<BankPreAccSetStatusResult>>(`bank-pre-accounting/take-back`, {
        statements,
      })
      .then(handleApiResponse);
  }

  //---------- Kontír-szerkesztő ("Adott bankkivonat kontírozási tételei") ----------//

  // A szerkesztő teljes betöltési csomagja (fej + tételek + bontás + lookupok)
  static getEditorData(id: number): Promise<BankPreAccEditorData> {
    return http
      .get<ApiResponse<BankPreAccEditorData>>(`bank-pre-accounting/${id}/editor-data`)
      .then(handleApiResponse);
  }

  // A kontír-szerkesztő mentése (K2D pb_ok) — a szerkesztő nyitva marad, csak újraolvas
  static save(id: number, request: BankPreAccSaveRequest): Promise<BankPreAccSaveResult> {
    return http
      .post<ApiResponse<BankPreAccSaveResult>>(`bank-pre-accounting/${id}/save`, request)
      .then(handleApiResponse);
  }

  //---------- Feladás-tételek (K2D pb_felad → w_popup_item_up_os) ----------//

  // A kivonat banki tételei a feladás-pipával
  static getUploadItems(id: number): Promise<BankPreAccUploadItem[]> {
    return http
      .get<ApiResponse<BankPreAccUploadItem[]>>(`bank-pre-accounting/${id}/upload-items`)
      .then(handleApiResponse);
  }

  // A feladás-pipák mentése (csak a változott tételeket küldjük)
  static saveUploadFlags(
    id: number,
    items: { accountItemId: number; isUp: boolean }[]
  ): Promise<BankPreAccUploadFlagsResult> {
    return http
      .post<ApiResponse<BankPreAccUploadFlagsResult>>(`bank-pre-accounting/${id}/upload-flags`, {
        items,
      })
      .then(handleApiResponse);
  }

  //---------- A dimenzió-bontás füleinek kísérő-lookupjai ----------//

  static getEditorCars(): Promise<BankPreAccCar[]> {
    return http
      .get<ApiResponse<BankPreAccCar[]>>(`bank-pre-accounting/editor-lookups/cars`)
      .then(handleApiResponse);
  }

  static getEditorProjects(): Promise<BankPreAccProject[]> {
    return http
      .get<ApiResponse<BankPreAccProject[]>>(`bank-pre-accounting/editor-lookups/projects`)
      .then(handleApiResponse);
  }

  static getEditorPos(): Promise<BankPreAccPo[]> {
    return http
      .get<ApiResponse<BankPreAccPo[]>>(`bank-pre-accounting/editor-lookups/pos`)
      .then(handleApiResponse);
  }

  static getEditorUsers(): Promise<{ id: number; name: string }[]> {
    return http
      .get<ApiResponse<{ id: number; name: string }[]>>(`bank-pre-accounting/editor-lookups/users`)
      .then(handleApiResponse);
  }

  static getEditorStations(): Promise<{ id: number; name: string }[]> {
    return http
      .get<ApiResponse<{ id: number; name: string }[]>>(`bank-pre-accounting/editor-lookups/stations`)
      .then(handleApiResponse);
  }

  static getEditorTools(): Promise<BankPreAccTool[]> {
    return http
      .get<ApiResponse<BankPreAccTool[]>>(`bank-pre-accounting/editor-lookups/tools`)
      .then(handleApiResponse);
  }

  static validatePart(code: string): Promise<BankPreAccValidate> {
    return http
      .get<ApiResponse<BankPreAccValidate>>(
        `bank-pre-accounting/validate/part/${encodeURIComponent(code)}`)
      .then(handleApiResponse);
  }

  static validatePartner(name: string): Promise<BankPreAccValidate> {
    return http
      .get<ApiResponse<BankPreAccValidate>>(
        `bank-pre-accounting/validate/partner/${encodeURIComponent(name)}`)
      .then(handleApiResponse);
  }
}
